import React, { useState } from "react";
import { Checkbox, Icon, InputAdornment } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import { useDraftContext } from "./DraftContext";
import { draftColors, draftTypography } from "./draftDesignTokens";
import { CostColumn } from "./costColumnDefinitions";

export type CostRow = Record<string, any> & {
  __columnOverrides?: Record<string, Partial<CostColumn>>;
  __controlled?: Record<string, { value: boolean; onChange: (checked: boolean) => void }>;
  __rowIndex?: number;
  __rowKey?: string | number;
};

type CostSectionProps = {
  title: string;
  total: string;
  actionLabel?: string;
  onActionClick?: () => void;
  showAdd?: boolean;
  onAdd?: () => void;
  columns: CostColumn[];
  rows: CostRow[];
  onFieldChange?: (rowIndex: number, columnKey: string, value: string) => void;
  onCheckboxChange?: (rowIndex: number, columnKey: string, checked: boolean) => void;
  onDelete?: (rowIndex: number) => void;
  className?: string;
};

const adornmentSx = {
  "& .MuiTypography-root": {
    fontSize: draftTypography.body12.fontSize,
    color: draftColors.textSecondary,
  },
};

function buildAdornments(column: CostColumn) {
  const props: Record<string, React.ReactNode> = {};
  if (column.startAdornment) {
    props.startAdornment = (
      <InputAdornment position="start" sx={adornmentSx}>
        {column.startAdornment}
      </InputAdornment>
    );
  }
  if (column.endAdornment) {
    props.endAdornment = (
      <InputAdornment position="end" sx={adornmentSx}>
        {column.endAdornment}
      </InputAdornment>
    );
  }
  return props;
}

function CostCell({
  column,
  row,
  rowIndex,
  isStaticReadOnly,
  isOverrideDisabled,
  onFieldChange,
  onCheckboxChange,
  onDelete,
}: {
  column: CostColumn;
  row: CostRow;
  rowIndex: number;
  isStaticReadOnly: boolean;
  isOverrideDisabled: boolean;
  onFieldChange?: CostSectionProps["onFieldChange"];
  onCheckboxChange?: CostSectionProps["onCheckboxChange"];
  onDelete?: CostSectionProps["onDelete"];
}) {
  const { isReadOnly } = useDraftContext();
  const effectiveRowIndex = row.__rowIndex ?? rowIndex;

  if (column.type === "spacer") {
    return <MDBox />;
  }

  if (column.key === "__delete") {
    if (isReadOnly) return <MDBox />;
    return (
      <MDBox
        sx={{
          display: "flex",
          justifyContent: "center",
          color: draftColors.red,
          cursor: "pointer",
        }}
        className="tour-cost-delete"
        onClick={() => onDelete?.(effectiveRowIndex)}
      >
        <Icon fontSize="small">delete</Icon>
      </MDBox>
    );
  }

  if (column.type === "checkbox") {
    const [checked, setChecked] = useState<boolean>(!!row[column.key]);
    return (
      <MDBox sx={{ display: "flex", justifyContent: "center" }}>
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => {
            setChecked(e.target.checked);
            onCheckboxChange?.(effectiveRowIndex, column.key, e.target.checked);
          }}
          sx={{ p: 0 }}
        />
      </MDBox>
    );
  }

  if (isStaticReadOnly) {
    return (
      <MDBox minHeight="24px" display="flex" alignItems="center">
        <UiTypography
          uiVariant="body-sm"
          sx={{ textAlign: column.align ?? "left", px: "4px", width: "100%" }}
        >
          {row[column.key] ?? "-"}
        </UiTypography>
      </MDBox>
    );
  }

  const inputValue = String(row.__values?.[column.key] ?? "");
  const placeholder = column.placeholder ?? String(row[column.key] ?? "");
  const inputKey = [
    row.__rowKey ?? rowIndex,
    effectiveRowIndex,
    column.key,
    inputValue,
    placeholder,
  ].join("-");

  return (
    <UiInput
      key={inputKey}
      fullWidth
      placeholder={placeholder}
      defaultValue={inputValue}
      textAlign={column.align === "right" ? "right" : "left"}
      readOnly={isOverrideDisabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isOverrideDisabled) onFieldChange?.(effectiveRowIndex, column.key, e.target.value);
      }}
      InputProps={buildAdornments(column)}
    />
  );
}

export default function CostSection({
  title,
  total,
  actionLabel,
  onActionClick,
  showAdd = true,
  onAdd,
  columns,
  rows,
  onFieldChange,
  onCheckboxChange,
  onDelete,
  className,
}: CostSectionProps) {
  const { isReadOnly } = useDraftContext();
  const gridTemplate = columns
    .map((col) => (col.key === "__delete" ? "32px" : `${col.flex ?? 1}fr`))
    .join(" ");

  return (
    <UiCard variant="section" className={className}>
      <MDBox
        display="grid"
        gridTemplateColumns={gridTemplate}
        alignItems="center"
        sx={{
          borderBottom: `1px solid ${draftColors.border}`,
          backgroundColor: draftColors.surfaceTableHeader,
          py: "4px",
        }}
      >
        {columns.map((column) => {
          if (column.key === "__delete") {
            return (
              <MDBox
                key={`${title}-${column.key}-header`}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                {showAdd && !isReadOnly && (
                  <MDBox
                    className="tour-cost-add"
                    sx={{
                      color: draftColors.green,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={onAdd}
                  >
                    <Icon fontSize="small">add</Icon>
                  </MDBox>
                )}
              </MDBox>
            );
          }
          if (column.type === "spacer") {
            return <MDBox key={`${title}-${column.key}-header`} />;
          }
          return (
            <UiTypography
              key={`${title}-${column.key}-header`}
              uiVariant="overline"
              sx={{ px: "8px", textAlign: column.align ?? "left" }}
            >
              {column.label}
            </UiTypography>
          );
        })}
      </MDBox>

      {rows.map((row, index) => {
        const overrides = row.__columnOverrides ?? {};
        const rowKey = row.__rowKey ?? `${title}-${index}`;

        return (
          <MDBox
            key={rowKey}
            display="grid"
            gridTemplateColumns={gridTemplate}
            alignItems="center"
            sx={{
              borderBottom: `1px solid ${draftColors.border}`,
              py: "4px",
            }}
          >
            {columns.map((baseColumn) => {
              const column = { ...baseColumn, ...overrides[baseColumn.key] };
              const isStaticReadOnly = !!baseColumn.readOnly;
              const isOverrideDisabled = !isStaticReadOnly && !!overrides[baseColumn.key]?.readOnly;

              return (
                <MDBox
                  key={`${title}-${index}-${column.key}`}
                  px="4px"
                  sx={{ display: "flex", alignItems: "center", "& > *": { width: "100%" } }}
                >
                  <CostCell
                    column={column}
                    row={row}
                    rowIndex={index}
                    isStaticReadOnly={isStaticReadOnly}
                    isOverrideDisabled={isOverrideDisabled}
                    onFieldChange={onFieldChange}
                    onCheckboxChange={onCheckboxChange}
                    onDelete={onDelete}
                  />
                </MDBox>
              );
            })}
          </MDBox>
        );
      })}

      <MDBox px="8px" py="6px" display="flex" justifyContent="space-between" alignItems="center">
        <MDBox>
          {actionLabel && (
            <UiButton className="tour-cost-action" uiVariant="primary-grey" onClick={onActionClick}>
              {actionLabel}
            </UiButton>
          )}
        </MDBox>
        <UiTypography uiVariant="accent-bold" sx={{ pr: "28px" }}>
          {total}
        </UiTypography>
      </MDBox>
    </UiCard>
  );
}
